import { QueueFilters, QueueTab, QueueView, SenderType } from "./queueTypes";

/** The Open / Resolved tabs (KAN-1325) of each view that has them: [Open, Resolved]. */
export const STATE_TABS: Partial<Record<QueueTab, [QueueTab, QueueTab]>> = {
  assignedToMe: ["assignedToMe", "assignedToMeResolved"],
  assignedToMeResolved: ["assignedToMe", "assignedToMeResolved"],
  exception: ["exception", "exceptionResolved"],
  exceptionResolved: ["exception", "exceptionResolved"],
  group: ["group", "groupResolved"],
  groupResolved: ["group", "groupResolved"],
};

/** The view's Open / Resolved tabs; none on Unassigned. */
export const stateTabsFor = (tab: QueueTab): { id: QueueTab; label: string }[] => {
  const pair = STATE_TABS[tab];
  return pair
    ? [
        { id: pair[0], label: "Open" },
        { id: pair[1], label: "Resolved" },
      ]
    : [];
};

/** A Resolved tab (KAN-1325): its tickets are resolved / closed. */
export const isResolvedTab = (tab: QueueTab) => STATE_TABS[tab]?.[1] === tab;

/** Exception's Open and Resolved tabs (KAN-1347: Carrier Name in place of Who Is Writing). */
export const isExceptionView = (tab: QueueTab) => tab === "exception" || tab === "exceptionResolved";

/** Inside one group: Exception or a viewed group (KAN-1362); every ticket listed is that group's. */
export const isGroupView = (tab: QueueTab) =>
  isExceptionView(tab) || tab === "group" || tab === "groupResolved";

/** Where each view lives: also its Communications menu entry (as designed), so the view follows the URL. */
const FIXED_ROUTES: Partial<Record<QueueTab, string>> = {
  unassigned: "/communications/unassigned",
  assignedToMe: "/communications/assigned-to-me",
  assignedToMeResolved: "/communications/assigned-to-me/resolved",
  exception: "/communications/exceptions",
  exceptionResolved: "/communications/exceptions/resolved",
};

/** A viewed group's listing (KAN-1362); its Resolved tab is under it. */
export const GROUP_ROUTE_PREFIX = "/communications/groups";

export const groupRoute = (groupId: string) => `${GROUP_ROUTE_PREFIX}/${encodeURIComponent(groupId)}`;

export const queueRoute = ({ tab, groupId = "" }: QueueView): string => {
  if (tab === "group") return groupRoute(groupId);
  if (tab === "groupResolved") return `${groupRoute(groupId)}/resolved`;
  return FIXED_ROUTES[tab] ?? "/communications/unassigned";
};

/** The listing at `pathname`; null when it is none. */
export const queueViewFromPath = (pathname: string): QueueView | null => {
  const fixed = (Object.keys(FIXED_ROUTES) as QueueTab[]).find((tab) => FIXED_ROUTES[tab] === pathname);
  if (fixed) return { tab: fixed };
  const group = pathname.match(/^\/communications\/groups\/([^/]+)(\/resolved)?$/);
  if (!group) return null;
  return { tab: group[2] ? "groupResolved" : "group", groupId: decodeURIComponent(group[1]) };
};

/** The listing a ticket at `pathname` was opened from (its route + "/" + the ticket id). */
export const queueViewFromTicketPath = (pathname: string): QueueView =>
  queueViewFromPath(pathname.slice(0, pathname.lastIndexOf("/"))) ?? { tab: "unassigned" };

/**
 * Each view's key in the filter slice: its applied filters and page size are remembered per view (each group its
 * own), as the system filter remembers them per screen (Decision D3, Ticket Queue Filters).
 */
export const queueScreenKey = ({ tab, groupId }: QueueView) =>
  groupId ? `ticketQueue_${tab}_${groupId}` : `ticketQueue_${tab}`;

/** A viewed group's navbar title, e.g. "Group - Billing" (its route alone gives "Groups"); null until the name is known. */
export const groupPageTitle = (groupName?: string) => (groupName ? `Group - ${groupName}` : null);

/** Back from a ticket opened under each view; a group's under its name. */
export const queueBackLabel = ({ tab }: QueueView, groupName?: string) => {
  if (tab === "group" || tab === "groupResolved") return `Back to ${groupName || "Group"}`;
  if (isExceptionView(tab)) return "Back to Exceptions";
  return tab === "unassigned" ? "Back to Unassigned" : "Back to Assigned to Me";
};

/** "Who Is Writing" options (KAN-1330), in the story's order. */
export const SENDER_TYPE_OPTIONS: { value: SenderType; label: string }[] = [
  { value: "unassigned", label: "Unassigned" },
  { value: "customer", label: "Customer" },
  { value: "consignee", label: "Consignee" },
  { value: "carrier", label: "Carrier" },
  { value: "dtsTeam", label: "DTS Team" },
  // The order's origin contact (shipper) on LTL / LTL + Final Mile orders
  { value: "origin", label: "Origin" },
];

/** The value for "no group" / "no region" in dropdowns and filters. */
export const UNASSIGNED = "unassigned";
export const UNASSIGNED_LABEL = "Unassigned";

export const EMPTY_VALUE = "--";

/** No filter applied; also the key order of the filter params on GET /helpdesk/tickets. */
export const EMPTY_FILTERS: QueueFilters = {
  region: [],
  priority: [],
  status: [],
  group: [],
  userType: [],
};

export interface QueueColumn {
  key: string;
  label: string;
  /** Heading and cells centred, as designed. */
  centered?: boolean;
}

/** In the design's order; TicketRow draws its cells in the same order. */
export const TICKET_TABLE_COLUMNS: QueueColumn[] = [
  { key: "sender", label: "Who Is Writing" },
  { key: "subject", label: "Subject" },
  { key: "owner", label: "Owner", centered: true },
  { key: "status", label: "Status", centered: true },
  { key: "priority", label: "Priority", centered: true },
  { key: "order", label: "Order Num", centered: true },
  { key: "group", label: "Group", centered: true },
  { key: "region", label: "Region", centered: true },
  { key: "timestamp", label: "Timestamp" },
];

/**
 * Exception (KAN-1347): the same columns, but Carrier Name in place of Who Is Writing ("-" when the ticket
 * has no carrier).
 */
export const EXCEPTION_TABLE_COLUMNS = TICKET_TABLE_COLUMNS.map((column) =>
  column.key === "sender" ? { key: "carrier", label: "Carrier Name" } : column
);

/** A viewed group lists the Unassigned listing's columns (KAN-1362). */
export const columnsFor = (tab: QueueTab) => (isExceptionView(tab) ? EXCEPTION_TABLE_COLUMNS : TICKET_TABLE_COLUMNS);

export const EMPTY_TEXT: Record<QueueTab, string> = {
  unassigned: "No unassigned tickets — the queue is clear.",
  assignedToMe: "No open tickets are assigned to you.",
  assignedToMeResolved: "No resolved tickets are assigned to you.",
  exception: "No exception tickets.",
  exceptionResolved: "No resolved exception tickets.",
  group: "No open tickets in this group.",
  groupResolved: "No resolved tickets in this group.",
};

/** Exception listing's empty Carrier Name (KAN-1347). */
export const NO_CARRIER = "-";
